import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from './account.service';
import { Subject, Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { IDLE_LOGOUT_CONFIG, USER_ACTIVITY_EVENTS, getEnvironmentConfig } from './idle-logout.config';
import { OpenDialog } from '../shared/dialogs/dialog';
import { IdleCountdownComponent, IdleCountdownData } from '../shared/idle-countdown/idle-countdown.component';

export interface IdleConfig {
  timeout: number; // in milliseconds
  warningTime?: number; // in milliseconds
  events?: string[];
}

export interface ModuleIdleConfig {
  [moduleName: string]: IdleConfig;
}

@Injectable({
  providedIn: 'root',
})
export class IdleLogoutService implements OnDestroy {
  private idleTimer: any;
  private warningTimer: any;
  private isIdle = false;
  private currentConfig: IdleConfig;
  private destroy$ = new Subject<void>();
  private userActivity$ = new Subject<void>();
  private subscription: Subscription;
  private countdownDialog: any = null;

  // Default configurations for different modules/pages
  private readonly defaultConfigs: ModuleIdleConfig = getEnvironmentConfig();

  // Events to monitor for user activity
  private readonly defaultEvents = USER_ACTIVITY_EVENTS;

  constructor(private router: Router, private accountService: AccountService) {
    this.setupActivityMonitoring();
  }

  /**
   * Initialize idle logout for a specific module/page
   * @param moduleName - The name of the module/page
   * @param customConfig - Optional custom configuration
   */
  initializeIdleLogout(moduleName: string, customConfig?: Partial<IdleConfig>): void {
    // Get configuration for the module
    const moduleConfig = this.defaultConfigs[moduleName] || this.defaultConfigs['default'];
    console.log(moduleConfig, moduleName);
    // Merge with custom config if provided
    this.currentConfig = {
      ...moduleConfig,
      ...customConfig,
      events: customConfig?.events || moduleConfig.events || this.defaultEvents,
    };

    this.resetIdleTimer();
  }

  /**
   * Disable idle logout
   */
  disableIdleLogout(): void {
    this.clearTimers();
    this.isIdle = false;
    this.closeCountdownDialog();
  }

  /**
   * Update timeout for current module
   * @param timeout - New timeout in milliseconds
   */
  updateTimeout(timeout: number): void {
    if (this.currentConfig) {
      this.currentConfig.timeout = timeout;
      this.resetIdleTimer();
    }
  }

  /**
   * Update warning time for current module
   * @param warningTime - New warning time in milliseconds
   */
  updateWarningTime(warningTime: number): void {
    if (this.currentConfig) {
      this.currentConfig.warningTime = warningTime;
    }
  }

  /**
   * Reset the idle timer (called when user activity is detected)
   */
  resetIdleTimer(): void {
    this.clearTimers();
    this.isIdle = false;

    if (!this.currentConfig) {
      return;
    }

    // Set main idle timer
    this.idleTimer = setTimeout(() => {
      this.handleIdleTimeout();
    }, this.currentConfig.timeout);
    console.log(this.currentConfig.timeout);
    // Set warning timer if configured
    if (this.currentConfig.warningTime && this.currentConfig.warningTime < this.currentConfig.timeout) {
      this.warningTimer = setTimeout(() => {
        this.handleWarning();
      }, this.currentConfig.timeout - this.currentConfig.warningTime);
    }
  }

  /**
   * Setup activity monitoring
   */
  private setupActivityMonitoring(): void {
    // Create observables for all activity events
    const activityEvents = this.defaultEvents.map((event) => fromEvent(document, event));

    // Merge all events and debounce to avoid excessive calls
    this.subscription = merge(...activityEvents)
      .pipe(
        debounceTime(1000), // Debounce to 1 second
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.userActivity$.next();
        this.resetIdleTimer();
      });

    // Also listen to route changes
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.userActivity$.next();
      this.resetIdleTimer();
    });
  }

  /**
   * Handle idle timeout - perform logout
   */
  private async handleIdleTimeout(): Promise<void> {
    if (this.isIdle) {
      return; // Already handled
    }

    this.isIdle = true;
    console.log('User idle timeout reached. Logging out...');

    // Close the countdown dialog if it's open
    this.closeCountdownDialog();
    console.log('Countdown dialog closed');

    try {
      // Get current user for proper logout handling
      const currentUser = await this.accountService.currentUserAsync();

      // Perform logout
      await this.accountService.signOut();

      // Navigate based on user role
      if (currentUser?.roles?.some((role: string) => role === 'ROLE_ADMIN')) {
        this.router.navigate(['/admin-login']);
      } else {
        this.router.navigate(['/signin']);
      }
    } catch (error) {
      console.error('Error during idle logout:', error);
      // Fallback logout
      localStorage.clear();
      this.router.navigate(['/signin']);
    }
  }

  /**
   * Handle warning - show countdown dialog
   */
  private async handleWarning(): Promise<void> {
    console.log('User will be logged out soon due to inactivity...');

    if (this.countdownDialog) {
      return; // Dialog already open
    }

    const warningTime = this.currentConfig.warningTime || 5 * 60 * 1000; // 5 minutes default

    const countdownData: IdleCountdownData = {
      remainingTime: warningTime,
      onExtend: () => {
        this.extendSession();
      },
      onLogout: () => {
        this.handleIdleTimeout();
      },
    };

    try {
      const result = await OpenDialog(IdleCountdownComponent, countdownData);
      // If dialog is closed without action, proceed with logout
      if (!result) {
        this.handleIdleTimeout();
      }
    } catch (error) {
      console.error('Error opening countdown dialog:', error);
      // Fallback to direct logout
      this.handleIdleTimeout();
    }
  }

  /**
   * Extend session - reset timer and close dialog
   */
  private extendSession(): void {
    console.log('Session extended by user');
    this.closeCountdownDialog();
    this.resetIdleTimer();
  }

  /**
   * Close countdown dialog
   */
  private closeCountdownDialog(): void {
    if (this.countdownDialog) {
      this.countdownDialog.close();
      this.countdownDialog = null;
    }
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    this.closeCountdownDialog();
  }

  /**
   * Get current idle status
   */
  getIsIdle(): boolean {
    return this.isIdle;
  }

  /**
   * Get current configuration
   */
  getCurrentConfig(): IdleConfig | null {
    return this.currentConfig;
  }

  /**
   * Get remaining time until logout
   */
  getRemainingTime(): number {
    if (!this.currentConfig || !this.idleTimer) {
      return 0;
    }

    // This is a simplified calculation - in a real implementation,
    // you'd want to track the actual start time
    return this.currentConfig.timeout;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTimers();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
